import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { CustomInput, CustomSelect } from '../Components/CustomFormElements';
import { AgentFrameApiProvider, useAgentFrameApi } from '../Services/AgentFrameApiProvider';
import {
  AssistantFrameApi,
  AssistantFrameBulkDeleteByFilter,
  AssistantFrameExportApi,
  AssistantFrameFilters,
} from '../Services/AgentFrameApiProviderTypes';
import { ThematicApiProvider, useThematicApi } from '../Services/ThematicApiProvider';
import { formatDateUTC } from '../utils/formatDate';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';
import { formatFrameType } from './AgentHelpers';
import SplitButton from '../Components/SplitButton';

export function AgentFramesContent({ canEdit }: { canEdit: boolean }) {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const {
    getAgentFrames,
    getAgentFrameById,
    bulkDeleteAgentFrames,
    createAgentFrame,
    importAgentFrames,
    exportAgentFrames,
  } = useAgentFrameApi();
  const { getThematic } = useThematicApi();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AssistantFrameApi[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [selectedRows, setSelectedRows] = useState<AssistantFrameApi[]>([]);
  const [clearSelectedToggle, setClearSelectedToggle] = useState<boolean>(false);
  const selectedId = selectedRows.length === 1 ? selectedRows[0].id : null;

  // Filters
  const [filters, setFilters] = useState<AssistantFrameFilters>({
    name: '',
    instruction: '',
    topic: '',
    criteria: '',
    thematics: [],
  });
  const [thematicsOptions, setThematicsOptions] = useState<{ value: string; label: string }[]>([]);
  const hasActiveFilters =
    (filters.name ?? '').trim() !== '' ||
    (filters.instruction ?? '').trim() !== '' ||
    (filters.topic ?? '').trim() !== '' ||
    (filters.criteria ?? '').trim() !== '' ||
    (filters.thematics?.length ?? 0) > 0;

  useEffect(() => {
    async function fetchThematics() {
      try {
        const data = await getThematic(0, 100);
        setThematicsOptions(data.items.map((item) => ({ value: item.key, label: item.name })));
      } catch (error) {
        console.error('Failed to load thematics:', error);
      }
    }
    fetchThematics();
  }, []);

  const tableTitle = 'Agent Frames';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const columns = [
    { name: 'ID', selector: (row: AssistantFrameApi) => row.id, sortable: false },
    { name: 'Name', selector: (row: AssistantFrameApi) => row.name, sortable: false },
    {
      name: 'Type',
      selector: (row: AssistantFrameApi) => formatFrameType(row.frame_type),
      sortable: false,
    },
    {
      name: 'Status',
      selector: (row: AssistantFrameApi) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
    {
      name: 'Created',
      selector: (row: AssistantFrameApi) => formatDateUTC(row.created_at),
      sortable: false,
    },
    {
      name: 'Updated',
      selector: (row: AssistantFrameApi) => formatDateUTC(row.updated_at),
      sortable: false,
    },
  ];

  const handleDelete = (selectedIds: string[]): void => {
    if (!selectedIds || selectedIds.length === 0) return;
    const count = selectedIds.length;
    const label = `agent frame${count > 1 ? 's' : ''}`;
    showConfirmation(`Are you sure you want to delete ${count} ${label}?`, async () => {
      try {
        setLoading(true);
        await bulkDeleteAgentFrames({ keys: selectedIds });
        clearSelection();
        await fetchItems(persistedSkip, persistedPageSize);
        showAlert('success', `${count} ${label} successfully deleted!`);
      } catch (error) {
        showAlert('fail', `Failed to delete ${count} ${label}!`);
      } finally {
        setLoading(false);
      }
    });
  };

  const clearSelection = (): void => {
    setSelectedRows([]);
    setClearSelectedToggle((prev) => !prev);
  };

  const handleDeleteAllByFilter = (): void => {
    const count = totalItems;
    const label = `agent frame${count === 1 ? '' : 's'}`;
    showConfirmation(
      `Are you sure you want to delete all ${count} ${label} matching the current filter?`,
      async () => {
        try {
          setLoading(true);
          await bulkDeleteAgentFrames({
            all_by_filter: true,
            name: filters.name?.trim() || null,
            instruction: filters.instruction?.trim() || null,
            topic: filters.topic?.trim() || null,
            criteria: filters.criteria?.trim() || null,
            thematics: filters.thematics && filters.thematics.length > 0 ? filters.thematics : null,
          } as AssistantFrameBulkDeleteByFilter);
          clearSelection();
          await fetchItems(persistedSkip, persistedPageSize);
          showAlert('success', `All ${count} ${label} successfully deleted!`);
        } catch (error) {
          showAlert('fail', `Failed to delete ${label} by filter!`);
        } finally {
          setLoading(false);
        }
      },
    );
  };

  const handleRowClicked = (row: AssistantFrameApi): void => {
    navigate(`/organizations/${organizationId}/agent-frames/${row.id}`, {
      state: { organizationId },
    });
  };

  const createCopy = async (): Promise<void> => {
    if (!selectedId) return;
    setLoading(true);

    try {
      const frameToCopy = await getAgentFrameById(selectedId);

      if (!frameToCopy) {
        showAlert('fail', 'Frame not found');
        setLoading(false);
        return;
      }

      const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      const baseName = frameToCopy.name;
      const safeBaseName = escapeRegExp(baseName);
      const copyNumbers = items
        .map((a) => {
          const match = a.name.match(new RegExp(`^${safeBaseName} \\(Copy (\\d+)\\)$`));
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((n) => n !== null);
      const nextCopyNumber = copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;

      const newFrameData = {
        name: `${baseName} (Copy ${nextCopyNumber})`,
        frame_type: frameToCopy.frame_type,
        topic: frameToCopy.topic ?? null,
        criteria: frameToCopy.criteria ?? null,
        thematics: frameToCopy.thematics ?? [],
        instruction: frameToCopy.instruction ?? null,
        example: frameToCopy.example ?? null,
        is_active: frameToCopy.is_active,
      };

      await createAgentFrame(newFrameData);

      await fetchItems(persistedSkip, persistedPageSize);

      showAlert('success', 'Frame copy was successfully created!');
    } catch (error) {
      console.error('Create copy error:', error);
      showAlert('fail', 'Failed to create a copy of the frame!');
    } finally {
      setLoading(false);
    }
  };
  const handleImportClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    // reset the input so selecting the same file again re-triggers the change event
    event.target.value = '';
    if (!file) return;
    setLoading(true);
    try {
      const response = await importAgentFrames(file);
      await fetchItems(persistedSkip, persistedPageSize);
      showAlert('success', `${response.totalCount} frame(s) were successfully imported!`);
    } catch (error) {
      const serverError = axios.isAxiosError(error)
        ? (error.response?.data as { error?: string; message?: string } | undefined)
        : undefined;
      const message =
        serverError?.error ??
        serverError?.message ??
        (error as Error)?.message ??
        'Failed to import frames';

      // Server error may contain dozens of lines (one per invalid row).
      // Trim it so the alert stays readable and doesn't overflow the screen.
      const MAX_LINES = 8;
      const lines = message.split('\n');
      const shortMessage =
        lines.length > MAX_LINES
          ? [...lines.slice(0, MAX_LINES), `…and ${lines.length - MAX_LINES} more error(s)`].join(
              '\n',
            )
          : message;

      showAlert('fail', shortMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleExportFrames = async (type: 'selected' | 'filter'): Promise<void> => {
    if (type === 'selected' && (!selectedRows || selectedRows.length === 0)) return;

    try {
      setLoading(true);
      let exportParams: AssistantFrameExportApi;

      if (type === 'selected') {
        exportParams = {
          keys: selectedRows.map((row) => row.id),
        };
      } else {
        exportParams = {
          name: filters.name?.trim() || null,
          instruction: filters.instruction?.trim() || null,
          topic: filters.topic?.trim() || null,
          criteria: filters.criteria?.trim() || null,
          thematics: filters.thematics && filters.thematics.length > 0 ? filters.thematics : null,
        };
      }

      const file = await exportAgentFrames(exportParams);

      const url = window.URL.createObjectURL(file);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'frames.xlsx';

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      if (type === 'selected') {
        clearSelection();
      }
    } catch (error) {
      console.error(`Error exporting ${type} frames:`, error);
      showAlert('fail', `Failed to export ${type} frames!`);
    } finally {
      setLoading(false);
    }
  };

  const exportContextActions = (
    <SplitButton
      options={[
        {
          label: 'Export Selected',
          onClick: () => handleExportFrames('selected'),
        },
        {
          label: `Export all matching filter (${totalItems})`,
          onClick: () => handleExportFrames('filter'),
        },
      ]}
      disabled={selectedRows.length === 0}
      variant="ghost"
      size="medium"
    />
  );

  const fetchItems = async (
    skip: number,
    limit: number,
    activeFilters: AssistantFrameFilters = filters,
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAgentFrames(skip, limit, activeFilters);
      setItems(response.items);
      setTotalItems(response.totalCount);
    } catch (error) {
      setError(error.message);
      showAlert('fail', 'Failed to fetch agents frames');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string): void => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleThematicsChange = (value: string | string[]): void => {
    setFilters((prev) => ({ ...prev, thematics: Array.isArray(value) ? value : [] }));
  };

  const handleFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') applyFilters();
  };

  const applyFilters = async (): Promise<void> => {
    clearSelection();
    await fetchItems(0, persistedPageSize, filters);
  };

  const resetFilters = async (): Promise<void> => {
    const cleared: AssistantFrameFilters = {
      name: '',
      instruction: '',
      topic: '',
      criteria: '',
      thematics: [],
    };
    setFilters(cleared);
    clearSelection();
    await fetchItems(0, persistedPageSize, cleared);
  };

  useEffect(() => {
    fetchItems(persistedSkip, persistedPageSize);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              {/* ---- Filters ---- */}
              <div className="p-2 border-yes radius-yes mb-3">
                <h4 className="h4-title">Filters</h4>
                <div className="gap d-flex folded">
                  <div className="filter-col">
                    <CustomInput
                      id="name"
                      label="Name"
                      placeholder="Filter by name"
                      value={filters.name ?? ''}
                      onChange={handleFilterChange}
                      onKeyDown={handleFilterKeyDown}
                    />
                  </div>

                  <div className="filter-col">
                    <CustomInput
                      id="instruction"
                      label="Instruction"
                      placeholder="Filter by instruction"
                      value={filters.instruction ?? ''}
                      onChange={handleFilterChange}
                      onKeyDown={handleFilterKeyDown}
                    />
                  </div>

                  <div className="filter-col">
                    <CustomInput
                      id="topic"
                      label="Topic"
                      placeholder="Filter by topic"
                      value={filters.topic ?? ''}
                      onChange={handleFilterChange}
                      onKeyDown={handleFilterKeyDown}
                    />
                  </div>

                  <div className="filter-col">
                    <CustomInput
                      id="criteria"
                      label="Criteria"
                      placeholder="Filter by criteria"
                      value={filters.criteria ?? ''}
                      onChange={handleFilterChange}
                      onKeyDown={handleFilterKeyDown}
                    />
                  </div>

                  <div className="filter-col">
                    <CustomSelect
                      id="thematics-filter"
                      label="Thematics"
                      options={thematicsOptions}
                      value={filters.thematics ?? []}
                      multiSelect={true}
                      onChange={handleThematicsChange}
                      placeholder="Select thematics"
                    />
                  </div>
                </div>

                <div className="d-flex gap mt-2">
                  <Button size="medium" background="color2" onClick={applyFilters}>
                    Apply filters
                  </Button>
                  <Button
                    size="medium"
                    variant="ghost"
                    onClick={resetFilters}
                    disabled={!hasActiveFilters}
                  >
                    Reset filters
                  </Button>
                </div>
              </div>

              {/* ---- Table ---- */}
              <Table
                title={tableTitle}
                columns={columns}
                data={items}
                selectableRows
                selectableRowsHighlight
                singleSelect={false}
                clearSelectedRows={clearSelectedToggle}
                paginationPerPage={persistedPageSize}
                progressPending={loading}
                paginationTotalRows={totalItems}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={(skip, limit) => fetchItems(skip, limit)}
                getRowUrl={(row) => `/organizations/${organizationId}/agent-frames/${row.id}`}
                onSelectedRowsChange={({ selectedRows }) => {
                  setSelectedRows(selectedRows);
                }}
                deleteAllByFilter={
                  canEdit
                    ? {
                        label: `Delete all matching filter (${totalItems})`,
                        onClick: handleDeleteAllByFilter,
                      }
                    : undefined
                }
                contextActionsExtra={exportContextActions}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/agent-frames/new-agent-frame`)
                  }
                >
                  Create New Agent Frame
                </Button>
              )}

              {canEdit && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,.csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleImportFile}
                  />
                  <Button
                    variant="ghost"
                    size="large"
                    onClick={handleImportClick}
                    disabled={loading}
                  >
                    Import frames
                  </Button>
                </>
              )}

              {selectedId && (
                <Button variant="ghost" size="medium" onClick={createCopy}>
                  Create copy
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function AgentFrames({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <ThematicApiProvider>
        <AgentFrameApiProvider>
          <AgentFramesContent canEdit={canEdit} />
        </AgentFrameApiProvider>
      </ThematicApiProvider>
    </ConfirmationProvider>
  );
}

export default AgentFrames;
